/**
 * FormService
 * 
 * Central hub service responsible for all form-related operations throughout the MacSafety
 * web application. Handles form submission, retrieval, listing, and state management
 * for the entire form system.
 * 
 * Key Features:
 * - Form submission with attachments and signatures
 * - Form data retrieval and caching
 * - Form list management with pagination, filtering, and sorting
 * - Loading state management and user feedback
 * - Error handling and authentication management
 * - HTML generation for PDF creation
 * - Signal-based state management for reactive UI updates
 * 
 * This service acts as the primary data layer for forms, managing:
 * 1. API communication for all form operations
 * 2. State management through Angular signals
 * 3. Loading states and user feedback
 * 4. Error handling and authentication refresh
 * 5. Integration with attachment and signature services
 * 
 * Data Architecture for form lists:
 * - formListResult signal contains only the most recent API response
 * - Components handle appending data locally for better performance
 * - No duplicate processing of existing entries on subsequent loads
 * 
 * Components that utilize this service:
 * - All individual form components: For form submission and data handling
 * - BaseFormComponent: For common form functionality
 * - FormLayoutComponent: For form structure and submission
 * - FormEntriesComponent: For displaying form lists
 * - FormViewComponent: For viewing individual form entries
 * - Any component requiring form data or submission capabilities
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  FormSubmissionData,
  FormSubmissionHttpRequest,
  FormSubmissionResult
} from '../models/forms/form-submission.model';
import { FormDocument, FormRetrievalResponse, FormRetrievalResponseWrapper } from '../models/forms/form-retrieval-response';
import { NearMissReport } from '../models/forms/near-miss-report/near-miss-report.model';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { FormRendererService } from './form-render.service';
import { Router } from '@angular/router';
import { AttachmentUploadService } from './attachment-upload.service';
import { ViewResponse } from '../models/form-entry-lists/view-response.model';
import { FormListResponse } from '../models/form-entry-lists/form-list-response.model';
import { createFormFieldMetadata } from '../utils/field-type-extractor';
import { FORM_REGISTRY } from '../models/form-registry';
import { TemplateListResponse } from '../models/form-entry-lists/template-list-response.model';
import { Form } from '@angular/forms';
import { TemplateSubmissionRequest } from '../models/forms/template-submission-request.model';

/**
 * Interface defining the structure of a form entry
 * Represents a submitted form with basic metadata and dynamic properties
 */
export interface FormEntry {
  id: string;           // Unique identifier for the form entry
  date: string;         // Submission date
  status: string;       // Current status of the form
  [key: string]: any;   // Allow for dynamic properties based on form type
}

@Injectable({
  providedIn: 'root'
})
export class FormService {
  // Private service dependencies
  private _httpClient: HttpClient;
  private _toastService: ToastService;
  private _entraService: EntraService;
  private _router: Router;
  private _attachmentService: AttachmentUploadService;
  private apiUrl: string;

  // Signals for reactive state management
  // Loading state with message for user feedback
  public dataLoading = signal<{ isLoading: boolean; message: string }>({
    isLoading: false,
    message: ''
  });

  // Form submission result for success/error handling
  public formSubmissionResult = signal<FormSubmissionResult | null>(null);
  
  // Current form type being processed
  public formEntryType = signal<string | null>(null);
  
  // Form list results for pagination and display
  public formListResult = signal<FormListResponse<any> | null>(null);
  
  // Individual form entry data for viewing
  public formEntryResponse = signal<FormRetrievalResponse<any> | null>(null);

  // Template list results for template management
  public templateListResult = signal<TemplateListResponse<any> | null>(null);

  // Current loaded template
  public currentTemplate = signal<FormDocument<any> | null>(null);

  constructor(
    http: HttpClient,
    toastService: ToastService,
    entraService: EntraService,
    router: Router,
    attachmentService: AttachmentUploadService,
    private formRendererService: FormRendererService
  ) {
    this._httpClient = http;
    this._toastService = toastService;
    this._entraService = entraService;
    this._router = router;
    this._attachmentService = attachmentService;
    this.apiUrl = environment.apiBaseUrl;
  }


    /**Opens a new tab to an external vehicle collision form */
  openExternalVehicleCollisionForm(): void {
    window.open(
      'https://forms.office.com/Pages/ResponsePage.aspx?id=nKDSpOUeBUWgrnPu1F1AkXpxYm-wJWlOr9U9Zfj1DR1UOVVBRjVSS0JJU1dURUJSRjZZRkJVQ1RURi4u',
      '_blank'
    );
  }

  /**
   * Clears the current form entry response from the signal
   * Useful for resetting state between different form operations
   */
  public clearFormEntryResponse(): void {
    this.formEntryResponse.set(null);
  }

  /**
   * Resets the data loading state to its initial values
   * Clears both the loading flag and any status messages
   */
  public resetDataLoading(): void {
    this.dataLoading.set({ isLoading: false, message: '' });
  }

  

  /**
   * Retrieves a specific form entry by type and ID
   * @param formType - The type of form
   * @param entryId - The unique identifier of the entry
   * @returns Observable of the specific form entry
   */
  getFormEntry(formType: string, entryId: string): Observable<FormEntry> {
    return this._httpClient.get<FormEntry>(`${this.apiUrl}/forms/${formType}/entries/${entryId}`);
  }

  /**
   * Submits a form with optional attachments and signatures
   * Handles loading states, HTML generation, and updates signals with the response
   * Integrates with FormRendererService to generate PDF-ready HTML content
   * 
   * @param request - The complete form submission request including data and attachments
   * @param formMetadata - Form structure information (steps, labels, title)
   * @param onSuccess - Optional callback for successful submission
   * @param onError - Optional callback for error handling
   */
  public async submitForm<T>(
    request: FormSubmissionData<T>,
    formMetadata: { formSteps: any[]; fieldLabels: Record<string, string>; formTitle: string },
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.dataLoading.set({ isLoading: true, message: 'Submitting form...' });

    // Get field metadata from the registry using formType
    const registryEntry = FORM_REGISTRY[request.formType];
    const fieldMetadata = registryEntry?.fieldMetadata;

    if (!fieldMetadata) {
      console.warn(`No field metadata found for form type: ${request.formType}`);
    }
    
    // Generate HTML without attachments (placeholder mode) for PDF generation
    const htmlWithPlaceholders = await this.formRendererService.generateFormHTMLWithoutAttachments({
      formEntry: request.formEntryData,
      formSteps: formMetadata.formSteps || [],
      fieldLabels: formMetadata.fieldLabels || {},
      formTitle: formMetadata.formTitle || '',
      fieldMetadata: fieldMetadata
    });

    this.PostForm(request, htmlWithPlaceholders).subscribe({
      next: (response: FormSubmissionResult) => {
        this.formSubmissionResult.set(response);
        
        if (response.success) {
          // Build consolidated success message
          const successParts: string[] = [];
          
          // Form submission success
          if (response.attachmentsUploaded > 0) {
            successParts.push(`Form submitted with ${response.attachmentsUploaded} attachments`);
          } else {
            successParts.push('Form submitted successfully');
          }
          
          // Email notification success
          if (response.emailSent && response.emailSentTo.length > 0) {
            successParts.push(`Email sent to ${response.emailSentTo.join(', ')}`);
          } else if (response.emailSent) {
            successParts.push('Email notification sent');
          }
          
          // Show consolidated success message
          const successMessage = successParts.join('. ');
          this._toastService.showSuccessMessage(successMessage);

          console.log('warning:', response.warning);
console.log('emailSent:', response.emailSent);
console.log('emailErrors:', response.emailErrors);

     // Show backend warning if present (e.g. PDF/email downstream failure)
  if (response.warning) {
    this._toastService.showWarningMessage(response.warning);
  } else if (!response.emailSent && response.emailErrors?.length > 0) {
    this._toastService.showWarningMessage(`Email notification failed: ${response.emailErrors.join(', ')}`);
  } else if (!response.emailSent) {
    this._toastService.showWarningMessage('Email notification failed to send');
  }

          if (onSuccess) {
            onSuccess();
          }
        } else {
          const errorMessage = response.uploadErrors?.length > 0
            ? `Failed to submit form: ${response.uploadErrors.join(', ')}`
            : response.message || 'Failed to submit form';
          this._toastService.showWarningMessage(errorMessage);
          this.dataLoading.set({ isLoading: false, message: '' });
        }
      },
      error: (error) => {
        console.error('Error submitting form:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showErrorMessage('An error occurred while submitting the form');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
        if (onError) {
          onError();
        }
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }


  /**
   * Fetches additional form entries by type and sets only the new entries in the signal
   * Used for loading more entries (pagination) - component handles appending
   * 
   * @param formType - The type of forms to retrieve
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by
   * @param sortDirection - Sort direction (ascending or descending)
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   */
  public fetchOffsetEntryListByType<T>(
    formType: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading more form entries...' });
    
    this.getFormListByType<T>(formType, offset, limit, sortColumn, sortDirection, filterColumn, filterValue, filterType).subscribe({
      next: (response: FormListResponse<T>) => {
        // Set only the new entries - component will handle appending
        this.formListResult.set(response);
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      error: (error) => {
        console.error('Error loading more form entries:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load more form entries');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Fetches additional form entries by submitter - returns only new entries
   * Used for loading more entries (pagination) - component handles appending
   * 
   * @param submitter - The name of the person who submitted the forms
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by (defaults to 'submissionDate')
   * @param sortDirection - Sort direction (defaults to 'desc')
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   */
  public fetchOffsetEntryListBySubmitter<AllFormsBySubmitterListItem>(
    submitter: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn: string = 'submissionDate',
    sortDirection: 'asc' | 'desc' = 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading more form entries...' });
    this.getFormListBySubmitter<AllFormsBySubmitterListItem>(submitter, offset, limit, sortColumn, sortDirection, filterColumn, filterValue, filterType).subscribe({
      next: (response: FormListResponse<AllFormsBySubmitterListItem>) => {
        // Simply set the new response - component will handle appending
        this.formListResult.set(response);
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      error: (error) => {
        console.error('Error loading more form entries:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load more form entries');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Gets the initial list of form entries by type and replaces the signal value
   * Used for the first load of form entries
   * 
   * @param formType - The type of forms to retrieve
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by
   * @param sortDirection - Sort direction (ascending or descending)
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   */
  public fetchInitialFormEntriesByType<T>(
    formType: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading form entries...' });
    this.formListResult.set(null);
    this.getFormListByType<T>(formType, offset, limit, sortColumn, sortDirection, filterColumn, filterValue, filterType).subscribe({
      next: (response: FormListResponse<T>) => {
        this.formListResult.set(response);
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      error: (error) => {
        console.error('Error loading form entries:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load form entries');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Gets the initial list of form entries by submitter and replaces the signal value
   * Used for the first load of form entries
   * 
   * @param submitter - The name of the person who submitted the forms
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by
   * @param sortDirection - Sort direction (ascending or descending)
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   */
  public fetchInitialFormEntriesBySubmitter<AllFormsBySubmitterListItem>(
    submitter: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading form entries...' });
    this.formListResult.set(null);
    this.getFormListBySubmitter<AllFormsBySubmitterListItem>(submitter, offset, limit, sortColumn, sortDirection, filterColumn, filterValue, filterType).subscribe({
      next: (response: FormListResponse<AllFormsBySubmitterListItem>) => {
        this.formListResult.set(response);
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      error: (error) => {
        console.error('Error loading form entries:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load form entries');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Fetches form data by ID and updates the formEntryResponse signal
   * Used for viewing individual form entries and their complete data
   * 
   * @param formId - The unique identifier of the form to fetch
   */
  public fetchForm<T>(formId: string): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading form data...' });
    this.formEntryResponse.set(null);

    this.GetForm(formId).subscribe({
      next: (response: FormRetrievalResponseWrapper<any>) => {
        this.formEntryResponse.set(response.response);
      },
      error: (error) => {
        console.error('Error fetching form:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load form data');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }


   /**
   * Fetches form data by ID and updates the formEntryResponse signal
   * Used for viewing individual form entries and their complete data
   * 
   * @param formId - The unique identifier of the form to fetch
   */
  public fetchFormTemplate<T>(formId: string): void {
    this.dataLoading.set({ isLoading: true, message: 'Loading form template data...' });
    this.currentTemplate.set(null);

    this.getTemplateById(formId).subscribe({
      // next: (response: FormRetrievalResponseWrapper<any>) => {
      //   this.currentTemplate.set(response.response);
      // },
           next: (response: any) => {
       console.log('Fetched template response:', response);
        this.currentTemplate.set(response.response);
      },
      error: (error) => {
        console.error('Error fetching form:', error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showWarningMessage('Failed to load form data');
        }
        
        this.dataLoading.set({ isLoading: false, message: '' });
      },
      complete: () => {
        this.dataLoading.set({ isLoading: false, message: '' });
      }
    });
  }





/**
 * Gets the initial list of template entries by type and replaces the signal value
 * Used for the first load of template entries
 * 
 * @param templateFormType - The template form type to retrieve
 */
public fetchTemplatesByType<T>(
  templateFormType: string
): void {
  this.dataLoading.set({ isLoading: true, message: 'Loading form templates...' });
  this.templateListResult.set(null);
  this.getTemplatesByType<T>(templateFormType).subscribe({
    next: (response: TemplateListResponse<T>) => {
      this.templateListResult.set(response);
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    error: (error) => {
      console.error('Error loading form templates:', error);``
      
      // Check for authentication errors
      if (error.status === 401 || error.status === 403) {
        this._entraService.sessionRefreshRequiredError();
      } else {
        this._toastService.showWarningMessage('Failed to load form templates');
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    complete: () => {
      this.dataLoading.set({ isLoading: false, message: '' });
    }
  });
}


/**
 * Creates a new template and updates relevant signals on success
 * Used when admin creates a template from scratch or from an existing form
 * 
 * @param request - The template submission request containing formType and formEntryData
 */
public submitTemplate<T>(request: TemplateSubmissionRequest<T>): void {
  this.dataLoading.set({ isLoading: true, message: 'Creating template...' });

  this.createTemplate<T>(request).subscribe({
    next: (response: any) => {
      console.log('Template created successfully:', response);
      
      if (response.success) {
        this._toastService.showSuccessMessage(response.message || 'Template created successfully');
        // Optionally refresh the template list
        this.fetchTemplatesByType<T>(request.formType);
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    error: (error) => {
      console.error('Error creating template:', error);
      
      // Check for authentication errors
      if (error.status === 401 || error.status === 403) {
        this._entraService.sessionRefreshRequiredError();
      } else {
        this._toastService.showWarningMessage('Failed to create template');
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    complete: () => {
      this.dataLoading.set({ isLoading: false, message: '' });
    }
  });
}

/**
 * Updates an existing template and shows success/error feedback
 * Used when admin edits template data or metadata (like template title)
 * 
 * @param templateId - The ID of the template to update
 * @param formType - The template form type (e.g., 'pfizer_pre_task_template')
 * @param templateData - The updated template form entry data
 */
public submitTemplateUpdate<T>(templateId: string, formType: string, templateData: T): void {
  this.dataLoading.set({ isLoading: true, message: 'Updating template...' });

  // Structure the payload to match backend expectations
  const payload = {
    id: templateId,
    formType: formType,
    formEntryData: templateData
  };

  this.updateTemplate<typeof payload>(templateId, formType, payload).subscribe({
    next: (response: any) => {
      console.log('Template updated successfully:', response);
      
      if (response.success) {
        this._toastService.showSuccessMessage(response.message || 'Template updated successfully');
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    error: (error) => {
      console.error('Error updating template:', error);
      
      // Check for authentication errors
      if (error.status === 401 || error.status === 403) {
        this._entraService.sessionRefreshRequiredError();
      } else {
        this._toastService.showWarningMessage('Failed to update template');
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    complete: () => {
      this.dataLoading.set({ isLoading: false, message: '' });
    }
  });
}

/**
 * Soft deletes a template by marking it as inactive
 * Used when admin removes a template from the system
 * Template is preserved for audit trails but hidden from active lists
 * 
 * @param templateId - The ID of the template to delete
 */
public submitTemplateDelete(templateId: string, formType: string): void {
  this.dataLoading.set({ isLoading: true, message: 'Deleting template...' });

  this.deleteTemplate(templateId, formType).subscribe({
    next: (response: any) => {
      console.log('Template deleted successfully:', response);
      
      if (response.success) {
        this._toastService.showSuccessMessage(response.message || 'Template deleted successfully');

        // Optionally refresh the template list to remove the deleted one
        this.fetchTemplatesByType(formType);
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    error: (error) => {
      console.error('Error deleting template:', error);
      
      // Check for authentication errors
      if (error.status === 401 || error.status === 403) {
        this._entraService.sessionRefreshRequiredError();
      } else {
        this._toastService.showWarningMessage('Failed to delete template');
      }
      
      this.dataLoading.set({ isLoading: false, message: '' });
    },
    complete: () => {
      this.dataLoading.set({ isLoading: false, message: '' });
    }
  });
}


  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // HTTP Request Methods
  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * HTTP POST request to submit a form with optional attachments and signatures
   * Generates HTML content for PDF creation and handles file uploads
   * 
   * @param request - Complete form submission data
   * @param htmlContent - Generated HTML content for PDF creation
   * @returns Observable of form submission result
   */
  private PostForm<T>(
    request: FormSubmissionData<T>,
    htmlContent: string
  ): Observable<FormSubmissionResult> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/submit-form`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        const formSubmissionData = new FormData();
        
        // Create the structured request object
        const requestData: FormSubmissionHttpRequest = {
          formType: request.formType,
          formEntryData: request.formEntryData,
          sendEmailNotification: true,
          htmlContent: htmlContent
        };
        
        // Add the JSON request data
        formSubmissionData.append('requestData', JSON.stringify(requestData));

        // Add file uploads
        if (request.attachments?.uploads) {
          Object.entries(request.attachments.uploads).forEach(([identifier, files]) => {
            files.forEach((file, index) => {
              formSubmissionData.append(`uploads.${identifier}.${index}`, file);
            });
          });
        }

        // Add signature files
        if (request.attachments?.signatures) {
          Object.entries(request.attachments.signatures).forEach(([identifier, files]) => {
            files.forEach((file, index) => {
              formSubmissionData.append(`signatures.${identifier}.${index}`, file);
            });
          });
        }

        return this._httpClient.post<FormSubmissionResult>(url, formSubmissionData, { headers });
      })
    );
  }
  
  /**
   * HTTP GET request to fetch form data by ID
   * Retrieves complete form entry data for viewing and editing
   * 
   * @param formId - The unique identifier of the form to retrieve
   * @returns Observable of form retrieval response wrapper
   */
  private GetForm<T>(formId: string): Observable<FormRetrievalResponseWrapper<T>> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/form-entry/${formId}`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        return this._httpClient.get<FormRetrievalResponseWrapper<T>>(url, { headers });
      })
    );
  }

  /**
   * Fetches form data by ID and returns the observable directly, without
   * touching the formEntryResponse signal or loading state.
   * Used by the bulk resend modal to process entries one at a time.
   *
   * @param formId - The unique identifier of the form to retrieve
   * @returns Observable of form retrieval response wrapper
   */
  public getFormEntryById<T>(formId: string): Observable<FormRetrievalResponseWrapper<T>> {
    return this.GetForm<T>(formId);
  }

  /**
   * HTTP PUT request to update an existing form
   * Allows modification of previously submitted form data
   * 
   * @param formType - The type of form being updated
   * @param id - The unique identifier of the form to update
   * @param data - The updated form data
   * @returns Observable of the update response
   */
  public updateForm<T>(formType: string, id: string, data: FormSubmissionData<T>): Observable<any> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/forms/${formType}/${id}`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        return this._httpClient.put(url, data, { headers });
      })
    );
  }

  /**
   * HTTP GET request to retrieve a list of forms by type with pagination, filtering, and sorting
   * Supports complex querying for form list displays
   * 
   * @param formType - The type of forms to retrieve
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by
   * @param sortDirection - Sort direction (ascending or descending)
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   * @returns Observable of paginated form list response
   */
  public getFormListByType<T>(
    formType: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): Observable<FormListResponse<T>> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
  

        let url = `${this.apiUrl}/V1/form-entries-by-type?formType=${encodeURIComponent(formType)}&offset=${offset}&limit=${limit}`;
    
        // Add filter parameters if provided
        if (filterColumn && filterValue) {
          url += `&filterKey=${encodeURIComponent(filterColumn)}&filterValue=${encodeURIComponent(filterValue)}`;
          if (filterType) {
            url += `&filterType=${encodeURIComponent(filterType)}`;
          }
        }
        
        // Add sort parameters if provided
        if (sortColumn) {
          url += `&sortBy=${encodeURIComponent(sortColumn)}`;
          if (sortDirection) {
            url += `&sortDirection=${sortDirection}`;
          }
        }

        
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });
        return this._httpClient.get<FormListResponse<T>>(url, { headers });
      })
    );
  }

  /**
   * HTTP GET request to retrieve a list of forms by submitter with pagination, filtering, and sorting
   * Supports complex querying for user-specific form displays
   * 
   * @param submitter - The name of the person who submitted the forms
   * @param offset - Offset for pagination
   * @param limit - Number of items per page
   * @param sortColumn - Column to sort by
   * @param sortDirection - Sort direction (ascending or descending)
   * @param filterColumn - Column to filter by
   * @param filterValue - Value to filter for
   * @param filterType - Type of filter to apply
   * @returns Observable of paginated form list response
   */
  public getFormListBySubmitter<AllFormsBySubmitterListItem>(
    submitter: string, 
    offset: number = 0,
    limit: number = 50,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    filterColumn?: string,
    filterValue?: string,
    filterType?: string,
  ): Observable<FormListResponse<AllFormsBySubmitterListItem>> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        let url = `${this.apiUrl}/V1/form-entries-by-submitter?submittedByName=${encodeURIComponent(submitter)}&offset=${offset}&limit=${limit}`;
        
        // Add filter parameters if provided
        if (filterColumn && filterValue) {
          url += `&filterKey=${encodeURIComponent(filterColumn)}&filterValue=${encodeURIComponent(filterValue)}`;
          if (filterType) {
            url += `&filterType=${encodeURIComponent(filterType)}`;
          }
        }
        
        // Add sort parameters if provided
        if (sortColumn) {
          url += `&sortBy=${encodeURIComponent(sortColumn)}`;
          if (sortDirection) {
            url += `&sortDirection=${sortDirection}`;
          }
        }
        
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });
        return this._httpClient.get<FormListResponse<AllFormsBySubmitterListItem>>(url, { headers });
      })
    );
  }


  /**
 * HTTP GET request to retrieve a list of templates by template form type
 * Returns all templates without pagination since template lists are small
 * 
 * @param templateFormType - The template form type to retrieve (e.g., 'pfizer_pre_task_template')
 * @returns Observable of template list response
 */
public getTemplatesByType<T>(
  templateFormType: string
): Observable<TemplateListResponse<T>> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${this.apiUrl}/V1/templates-by-type?templateFormType=${encodeURIComponent(templateFormType)}`;
      
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      return this._httpClient.get<TemplateListResponse<T>>(url, { headers });
    })
  );
}



/**
 * HTTP GET request to retrieve a single template by ID
 * Used when user selects a template to pre-populate a form or edit a template
 * 
 * @param templateId - The ID of the template to retrieve
 * @returns Observable of the template document
 */
public getTemplateById<T>(templateId: string): Observable<T> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${this.apiUrl}/V1/template/${encodeURIComponent(templateId)}`;
      
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      return this._httpClient.get<T>(url, { headers });
    })
  );
}

/**
 * HTTP POST request to create a new template
 * Used when admin creates a template from scratch or from an existing form
 * 
 * @param template - The template document to create
 * @returns Observable of the created template response
 */
public createTemplate<T>(templateSubmissionRequest: TemplateSubmissionRequest<T>): Observable<any> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${this.apiUrl}/V1/template`;
      
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      });
      return this._httpClient.post(url, templateSubmissionRequest, { headers });
    })
  );
}

/**
 * HTTP PUT request to update an existing template
 * Used when admin edits template data or metadata (like template title)
 * 
 * @param templateId - The ID of the template to update
 * @param formType - The template form type (e.g., 'pfizer_pre_task_template')
 * @param template - The updated template document (must include formType and formEntryData structure)
 */
public updateTemplate<T>(templateId: string, formType: string, template: T): Observable<any> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${this.apiUrl}/V1/template/${encodeURIComponent(templateId)}?formType=${encodeURIComponent(formType)}`;
      
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      });
      return this._httpClient.put(url, template, { headers });
    })
  );
}

/**
 * HTTP DELETE request to delete a template
 * Used when admin removes a template from the system
 * 
 * @param templateId - The ID of the template to delete
 * @returns Observable of the delete response
 */
public deleteTemplate(templateId: string, formType: string): Observable<any> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${this.apiUrl}/V1/template/${encodeURIComponent(templateId)}?formType=${encodeURIComponent(formType)}`;
      
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      return this._httpClient.delete(url, { headers });
    })
  );
}

}
